import { Children, cloneElement, MouseEvent, ReactElement, ReactNode, useCallback, KeyboardEvent } from 'react';

import { useSnapshot } from 'valtio';

import { useSelectModalStateContext } from '../SelectModal.state';

interface InjectedProps<U, Event> {
  readonly onClick: (...event: Event[]) => void;
  readonly selected: U;
}

interface SelectModalToggleCommonProps<T, U> {
  readonly isSelected?: (selected: Set<T>, ...items: T[]) => U;
}

interface SelectModalToggleItemProps<T> {
  readonly item: T;
  readonly items?: never;
}

interface SelectModalToggleItemsProps<T> {
  readonly item?: never;
  readonly items: T[] | Set<T>;
}

interface SelectModalToggleFunctionProps<U, E> {
  readonly asChild?: never;
  readonly children: ((props: InjectedProps<U, E>) => JSX.Element) | ReactNode;
}

interface SelectModalToggleAsChildProps<U, E> {
  readonly asChild: true;
  readonly children: ReactElement<Partial<InjectedProps<U, E>>>;
}

type SelectItemProps<T, U, E> = SelectModalToggleCommonProps<T, U> &
  (SelectModalToggleItemProps<T> | SelectModalToggleItemsProps<T>) &
  (SelectModalToggleFunctionProps<U, E> | SelectModalToggleAsChildProps<U, E>);

function defaultIsSelectedFn<T>(selected: Set<T>, ...items: T[]) {
  return items.every((i) => selected.has(i));
}

function SelectModalToggleFactory<T, U>(defaultIsSelected: (selected: Set<T>, ...items: T[]) => U) {
  function SelectModalToggle<TT extends T, E extends MouseEvent>({
    isSelected = defaultIsSelected,
    asChild,
    item,
    items,
    children,
  }: SelectItemProps<TT, U, E>) {
    const state = useSelectModalStateContext<TT>();
    const snapped = useSnapshot(state);
    const selected = isSelected(snapped.selected, ...(item ? [item] : [...(items as TT[])]));

    const onClick = useCallback(() => {
      if (item) {
        state.onToggle(item);
      }

      if (items) {
        state.onToggle(...items);
      }
    }, [state, item, items]);

    const onKeyUpHandler = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
          onClick();
        }
      },
      [onClick],
    );

    if (asChild) {
      return (
        <>
          {Children.map(children, (child) => {
            const originalOnClick = child.props.onClick;

            return cloneElement(child, {
              onClick(...args: E[]) {
                originalOnClick?.(...args);
                onClick();
              },
              selected,
            });
          })}
        </>
      );
    }

    if (typeof children === 'function') {
      return children({
        onClick,
        selected,
      });
    }

    return (
      <div role="button" tabIndex={0} onClick={onClick} onKeyUp={onKeyUpHandler} data-select-modal-toggle-selected={selected}>
        {children}
      </div>
    );
  }

  SelectModalToggle.Factory = SelectModalToggleFactory;

  return SelectModalToggle;
}

const SelectModalToggle = SelectModalToggleFactory(defaultIsSelectedFn);

export { SelectModalToggle };
